/* Persistent storefront shell: the header and its model-viewer never detach.
   Only GET page links are handled; forms, checkout and locale changes stay native. */
(function () {
  'use strict';
  if (window.FkxNavigation || !window.fetch || !window.AbortController ||
      (window.Shopify && Shopify.designMode)) return;

  const main = document.getElementById('MainContent');
  if (!main) return;
  window.FkxNavigation = { enabled: true };
  const scriptLoads = new Map();
  const globalStyles = new Set();
  const globalInlineStyles = new Set();
  let activeController;
  let sequence = 0;
  let renderedURL = location.href;
  let scrollTimer;
  let navigating = false;
  const executable = (script) => ['', 'text/javascript', 'application/javascript', 'module']
    .includes((script.getAttribute('type') || '').toLowerCase());
  const absolute = (url, base = location.href) => new URL(url, base).href;
  const pageKey = (url) => { const u = new URL(url); return u.origin + u.pathname + u.search; };
  // A link to the page already open (e.g. the current collection in the menu): fresh content, same header, no new history entry
  const isOpenPage = (url) => pageKey(url.href) === pageKey(location.href);

  function eligibleURL(anchor) {
    if (!anchor || !anchor.getAttribute('href') || anchor.hasAttribute('download') ||
        anchor.hasAttribute('data-no-ajax') || (anchor.target && anchor.target !== '_self') ||
        anchor.closest('form, facet-remove, .shopify-section--apps') ||
        anchor.rel.split(/\s+/).includes('external')) return null;
    const url = new URL(anchor.href, location.href);
    if (url.origin !== location.origin || !/^https?:$/.test(url.protocol)) return null;
    // A jump within the open page stays native; the open page itself is refreshed in place (see isOpenPage).
    if (isOpenPage(url) && url.hash) return null;
    // Prefix is the current Shopify locale, never a guessed language code.
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    if (!url.pathname.startsWith(root)) return null;
    const path = url.pathname.slice(root.length).replace(/^\//, '');
    if (!/^(?:$|(?:products|collections|pages|blogs|policies)(?:\/|$)|search\/?$|cart\/?$)/.test(path)) return null;
    if (/\.[a-z0-9]{2,5}$/i.test(path) || url.searchParams.has('section_id') ||
        url.searchParams.has('sections') || url.searchParams.has('preview_theme_id') ||
        url.searchParams.has('view')) return null;
    return url;
  }

  function saveScroll() {
    if (navigating) return;
    history.replaceState(Object.assign({}, history.state, {
      fkxNavigation: { url: location.href, x: scrollX, y: scrollY }
    }), '', location.href);
  }

  function loadNode(node, parent) {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => { node.remove(); reject(new Error('Asset timeout')); }, 10000);
      node.onload = () => { clearTimeout(timer); resolve(); };
      node.onerror = () => { clearTimeout(timer); node.remove(); reject(new Error('Asset unavailable')); };
      parent.appendChild(node);
    });
  }

  async function prepareStyles(doc, url, staged) {
    const urls = new Set(globalStyles);
    const jobs = [];
    doc.querySelectorAll('link[rel="stylesheet"][href]').forEach((source) => {
      const href = absolute(source.getAttribute('href'), url);
      if (urls.has(href)) return;
      urls.add(href);
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      // Load before replacing content; activate at commit to prevent a flash.
      link.media = 'not all';
      link.dataset.fkxPageStyle = '';
      link.dataset.fkxMedia = source.media === 'print' && source.hasAttribute('onload') ? 'all' : source.media || 'all';
      staged.push(link);
      jobs.push(loadNode(link, document.head));
    });
    doc.head.querySelectorAll('style').forEach((source) => {
      if (globalInlineStyles.has(source.textContent)) return;
      const style = source.cloneNode(true);
      style.dataset.fkxPageStyle = '';
      style.dataset.fkxMedia = source.media || 'all';
      style.media = 'not all';
      staged.push(style);
      document.head.appendChild(style);
    });
    await Promise.all(jobs);
  }

  function syncMetadata(doc) {
    document.title = doc.title;
    const selector = 'meta[name="description"],meta[name="robots"],meta[property^="og:"],meta[name^="twitter:"],link[rel="canonical"],link[rel="alternate"][hreflang]';
    document.head.querySelectorAll(selector).forEach((el) => el.remove());
    doc.head.querySelectorAll(selector).forEach((el) => document.head.appendChild(el.cloneNode(true)));
    // Keep runtime classes on body; refresh the page type used by theme styles.
    Array.from(document.body.classList).filter((name) => name.startsWith('template-')).forEach((name) => document.body.classList.remove(name));
    Array.from(doc.body.classList).filter((name) => name.startsWith('template-')).forEach((name) => document.body.classList.add(name));
    document.querySelectorAll('.section-header a[href]').forEach((link) => {
      const match = Array.from(doc.querySelectorAll('.section-header a[href]')).find((incoming) => incoming.getAttribute('href') === link.getAttribute('href'));
      if (!match) return;
      if (match.hasAttribute('aria-current')) link.setAttribute('aria-current', match.getAttribute('aria-current'));
      else link.removeAttribute('aria-current');
      link.classList.toggle('header__active-menu-item', match.classList.contains('header__active-menu-item'));
      const label = link.querySelector('span');
      if (label) label.classList.toggle('header__active-menu-item', !!match.querySelector('.header__active-menu-item'));
    });
  }

  function closeOverlays() {
    document.querySelectorAll('header-drawer, menu-drawer').forEach((drawer) => {
      if (drawer.querySelector('details[open]') && drawer.closeMenuDrawer) drawer.closeMenuDrawer(null);
    });
    document.querySelectorAll('cart-drawer.active').forEach((drawer) => drawer.close());
    document.querySelectorAll('modal-dialog[open], product-modal[open]').forEach((modal) => modal.hide());
    document.querySelectorAll('body > modal-dialog, body > product-modal, body > quick-add-modal').forEach((modal) => modal.remove());
    if (typeof removeTrapFocus === 'function') removeTrapFocus();
    document.body.classList.remove('overflow-hidden', 'overflow-hidden-mobile', 'overflow-hidden-tablet');
  }

  async function activateScripts(doc, url, token) {
    // Theme scripts define components once; their connectedCallback or ajax:loaded
    // initializes new content. Re-executing them would accumulate listeners.
    const headScripts = Array.from(doc.head.querySelectorAll('script[src]')).filter((s) => /\/cdn\/shop\/t\/|\/s\/files\/.*\/t\//.test(s.src));
    for (const source of headScripts.concat(Array.from(main.querySelectorAll('script')))) {
      if (token !== sequence) return;
      if (!executable(source)) continue; // Preserve variant JSON and structured data.
      const script = document.createElement('script');
      for (const attr of source.attributes) script.setAttribute(attr.name, attr.value);
      if (source.hasAttribute('src')) {
        const src = absolute(source.getAttribute('src'), url);
        if (!scriptLoads.has(src)) {
          script.src = src;
          script.async = false;
          script.removeAttribute('defer');
          const pending = loadNode(script, document.head).catch((error) => { scriptLoads.delete(src); throw error; });
          scriptLoads.set(src, pending);
        }
        await scriptLoads.get(src);
      } else {
        script.textContent = source.textContent;
        source.replaceWith(script);
      }
    }
  }

  function restorePosition(url, position) {
    const behavior = document.documentElement.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = 'auto';
    const hash = new URL(url).hash;
    let target;
    try { target = hash && document.getElementById(decodeURIComponent(hash.slice(1))); } catch (_) { /* malformed fragment */ }
    if (position) window.scrollTo(position.x || 0, position.y || 0);
    else if (target) target.scrollIntoView();
    else window.scrollTo(0, 0);
    document.documentElement.style.scrollBehavior = behavior;
  }

  // replace: keep the current history entry (back/forward, or the open page clicked again)
  async function navigate(url, replace, position) {
    if (!replace) saveScroll();
    clearTimeout(scrollTimer);
    if (activeController) activeController.abort();
    const controller = new AbortController();
    activeController = controller;
    const token = ++sequence;
    const staged = [];
    let committed = false;
    navigating = true;
    main.setAttribute('aria-busy', 'true');
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal, headers: { Accept: 'text/html' } });
      if (!response.ok || !(response.headers.get('content-type') || '').includes('text/html')) throw new Error('Not a storefront page');
      const destination = new URL(response.url || url);
      destination.hash = new URL(url).hash;
      if (destination.origin !== location.origin) throw new Error('External redirect');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const incoming = doc.getElementById('MainContent');
      if (!incoming || !doc.querySelector('script[src*="fkx-navigation.js"]') ||
          doc.documentElement.lang !== document.documentElement.lang) throw new Error('Different storefront shell');
      if (token !== sequence) return;
      await prepareStyles(doc, destination.href, staged);
      if (token !== sequence) return;
      if (controller.signal.aborted) throw new Error('Navigation timeout');
      closeOverlays();
      document.dispatchEvent(new CustomEvent('ajax:before', { detail: { url: destination.href } }));
      const oldStyles = Array.from(document.head.querySelectorAll('[data-fkx-page-style]')).filter((el) => !staged.includes(el));
      staged.forEach((el) => { el.media = el.dataset.fkxMedia; });
      if (replace) history.replaceState(history.state, '', destination.href);
      else history.pushState({ fkxNavigation: { url: destination.href, x: 0, y: 0 } }, '', destination.href);
      syncMetadata(doc);
      window.__PRODUCT_MEDIA_COLOR_FILTER_ACTIVE__ = !!incoming.querySelector('script[src*="product-media-color-filter.js"]');
      incoming.querySelectorAll('link[rel="stylesheet"]').forEach((el) => el.remove());
      main.innerHTML = incoming.innerHTML;
      oldStyles.forEach((el) => el.remove());
      renderedURL = destination.href;
      committed = true;
      restorePosition(destination.href, position);
      await activateScripts(doc, destination.href, token);
      if (token !== sequence) return;
      if (window.Shopify && Shopify.PaymentButton) Shopify.PaymentButton.init();
      document.dispatchEvent(new CustomEvent('ajax:loaded', { detail: { url: destination.href, main: main } }));
      main.focus({ preventScroll: true });
      requestAnimationFrame(() => { if (token === sequence) restorePosition(destination.href, position); });
    } catch (_) {
      if (token === sequence) location.assign(url);
    } finally {
      clearTimeout(timeout);
      if (!committed) staged.forEach((el) => el.remove());
      if (token === sequence) {
        navigating = false;
        main.removeAttribute('aria-busy');
        activeController = null;
      }
    }
  }

  function init() {
    window.FkxNavigation.visit = function (href) {
      const link = document.createElement('a');
      link.href = href;
      const url = eligibleURL(link);
      if (!url) return false;
      navigate(url.href, isOpenPage(url));
      return true;
    };
    document.querySelectorAll('script[src]').forEach((s) => { if (executable(s)) scriptLoads.set(s.src, Promise.resolve()); });
    document.head.querySelectorAll('link[rel="stylesheet"][href]').forEach((s) => globalStyles.add(s.href));
    document.head.querySelectorAll('style').forEach((s) => globalInlineStyles.add(s.textContent));
    history.scrollRestoration = 'manual';
    saveScroll();
    document.addEventListener('click', (event) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = event.target.closest && event.target.closest('a[href]');
      const url = eligibleURL(anchor);
      if (!url) return;
      event.preventDefault();
      navigate(url.href, isOpenPage(url));
    });
    window.addEventListener('scroll', () => { clearTimeout(scrollTimer); scrollTimer = setTimeout(saveScroll, 120); }, { passive: true });
    window.addEventListener('pagehide', saveScroll);
    window.addEventListener('popstate', (event) => {
      clearTimeout(scrollTimer);
      const position = event.state && event.state.fkxNavigation;
      if (pageKey(location.href) === pageKey(renderedURL)) {
        if (activeController) activeController.abort();
        sequence += 1;
        navigating = false;
        main.removeAttribute('aria-busy');
        restorePosition(location.href, position);
        return;
      }
      navigate(location.href, true, position);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
